import React, { useContext, useEffect } from "react";
import {
  RadioGroup as DeprecatedRadioGroup,
  RadioButton,
} from "../../../components/data_entry/radio";
import Input from "../../../components/data_entry/input";
import TextArea from "../../../components/data_entry/text_area";
import QuestionLabel from "../components/question_label";
import QuestionFieldsetLegend, { StyledFieldset } from "../components/question_fieldset_legend";
import { SurveyContext, SurveyDisplay } from "../../survey";
import styled from "styled-components";
import nextId from "react-id-generator";
import { getOptionObjectFromText, getOptionText, selectedOptionPresent } from "../question_helpers";
import { Answer, Customizations, getQuestionOptions, isOptional } from "./select_shared_utils";

const StyledFollowUp = styled.div`
  margin-top: 2em;
`;

export default function DeprecatedSelect({
  customizations,
  updateAnswer,
  currentAnswer,
  setAbleToSubmit,
}: {
  customizations: Customizations;
  updateAnswer: (newAnswer: Answer) => void;
  currentAnswer?: Answer;
  setAbleToSubmit: (ableToSubmit: boolean) => void;
}) {
  const { display } = useContext(SurveyContext);
  const id = nextId();

  const optional = isOptional(customizations);
  const selectedOption = currentAnswer?.selectedOption;

  // These options have the structure of { oid: string, text: string }[]
  const questionOptions = getQuestionOptions(
    customizations.options,
    customizations.withOtherOption,
  );
  const withOtherOption = customizations.withOtherOption;

  const followUp = customizations.followUp;
  const followUpOptionSelected = followUp && getOptionText(selectedOption) === followUp?.option;

  const optionalFollowUp =
    typeof followUp?.optional === "undefined" ? true : customizations.optional;

  const otherOptionSelected =
    withOtherOption && getOptionText(currentAnswer?.selectedOption) === withOtherOption.text;

  const valueProvidedForOtherOption =
    currentAnswer?.writeIn != null && currentAnswer.writeIn.length > 0;

  const valueProvidedForFollowUpOption = (currentAnswer?.followUpResponse?.length || 0) > 0;

  const followUpIsValid =
    !followUpOptionSelected || optionalFollowUp || valueProvidedForFollowUpOption;

  const otherOptionIsValid = !otherOptionSelected || valueProvidedForOtherOption;

  const answerIsValid =
    optional || (selectedOptionPresent(selectedOption) && otherOptionIsValid && followUpIsValid);

  useEffect(() => {
    setAbleToSubmit(answerIsValid);
  }, [answerIsValid, setAbleToSubmit]);

  return (
    <div className="multi-select">
      <StyledFieldset name={customizations.prompt}>
        <QuestionFieldsetLegend
          legend={customizations.prompt}
          description={customizations.description}
          questionIsOptional={optional}
          // We want to hide the required marker for micro surveys, but not on survey pages
          // because there is currently no error handling/feedback when submitting a survey without answering all required questions
          hideRequiredMarker={display === SurveyDisplay.banner}
          className="question-label"
          data-test="single-select-question-label"
        />
        {(display == SurveyDisplay.banner || display == SurveyDisplay.page) && (
          <DeprecatedRadioGroup
            name="selectQuestion"
            value={getOptionText(selectedOption)}
            onChange={(newValue) =>
              updateAnswer({ selectedOption: getOptionObjectFromText(newValue, questionOptions) })
            }
            role="group"
            aria-labelledby={id}
          >
            {questionOptions.map((option) => {
              const label = getOptionText(option) || "";
              return (
                <RadioButton
                  data-test="select-option"
                  key={label}
                  label={label}
                  value={label}
                  role="radio"
                />
              );
            })}
          </DeprecatedRadioGroup>
        )}
        {selectedOption && otherOptionSelected && (
          <Input
            type="text"
            value={currentAnswer && currentAnswer.writeIn ? currentAnswer.writeIn : ""}
            onChange={({ currentTarget: { value: updatedWriteIn } }) =>
              updateAnswer({
                selectedOption: selectedOption,
                writeIn: updatedWriteIn,
              })
            }
          />
        )}
        {followUp && followUpOptionSelected && (
          <StyledFollowUp>
            <QuestionLabel
              label={followUp.prompt}
              className="question-label"
              questionIsOptional={optionalFollowUp}
            />
            {followUp.includeOpenResponse && (
              <TextArea
                data-test="select-follow-up-input"
                rows={3}
                placeholder={followUp.placeholder ? followUp.placeholder : undefined}
                questionIsOptional={optionalFollowUp}
                onChange={({ currentTarget: { value: updatedResponse } }) =>
                  updateAnswer({
                    ...currentAnswer,
                    selectedOption: selectedOption,
                    followUpResponse: updatedResponse,
                  })
                }
                value={currentAnswer ? currentAnswer.followUpResponse : ""}
              />
            )}
          </StyledFollowUp>
        )}
      </StyledFieldset>
    </div>
  );
}
